import {
  useGetPresetMetrics,
  useGetPresetSections,
} from "@vega-api/generated/preset-templates/preset-templates";
import { type DealMetricType } from "@vega-api/generated/types/dealMetricType";
import { type PresetMetricResponse } from "@vega-api/generated/types/presetMetricResponse";
import { type PresetSectionResponse } from "@vega-api/generated/types/presetSectionResponse";
import { Tenant } from "@vega-core/constants/tenant";
import { type OptionWithId } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";

import {
  ACS_TEAM_CONTACT_NAME,
  MODAL_OPTION_VALUES,
  MODAL_TYPES,
  type ModalType,
} from "../constants/modalConstants";

export type CoinvestmentOverviewOption = OptionWithId & {
  metricType?: DealMetricType;
};

export const usePresetOptions = (
  modalType: ModalType,
  isOpen: boolean,
  existingNames: { name: string; metricType?: string }[] = [],
) => {
  const isMetric = modalType === MODAL_TYPES.METRIC;

  const { data: metricsResponse } = useGetPresetMetrics({
    query: {
      enabled: isOpen && isMetric,
    },
  });

  const { data: sectionsResponse } = useGetPresetSections({
    query: {
      enabled: isOpen && !isMetric,
    },
  });

  let responseData:
    | PresetMetricResponse[]
    | PresetSectionResponse[]
    | undefined = undefined;

  if (!isOpen) {
    responseData = undefined;
  } else if (isMetric) {
    responseData = metricsResponse?.data;
  } else {
    responseData = sectionsResponse?.data;
  }

  // TODO: Remove this hard-coded tenant check once this tech debt is resolved: https://vegainvestments.atlassian.net/browse/VD-26368
  const isApolloTenant = import.meta.env.VITE_TENANT === Tenant.APOLLO;

  const presetOptions: CoinvestmentOverviewOption[] =
    responseData
      ?.filter((item) => item.enabled && item.name)
      .filter((item) => isApolloTenant || item.name !== ACS_TEAM_CONTACT_NAME)
      .filter(
        (item) =>
          !existingNames.some(
            (existingItem) =>
              existingItem.name.toLowerCase() === item.name!.toLowerCase(),
          ),
      )
      .map((item) => ({
        value: item.name!,
        label: item.name!,
        metricType: "type" in item ? (item.type as DealMetricType) : undefined,
      })) || [];

  const options: CoinvestmentOverviewOption[] = [
    ...presetOptions,
    {
      value: MODAL_OPTION_VALUES.CUSTOM,
      label: m.coInvestments_lpDealOverviewContent_custom(),
    },
  ];

  return { options };
};
